/**
 * Shared E2EE (End-to-End Encryption) wire types.
 *
 * SCAFFOLDING ONLY — Phase 5. The whole feature is gated behind a global server
 * flag `E2EE_ENABLED` that defaults FALSE. When the flag is off the backend
 * endpoints are inert (404 / `{ enabled: false }`) and the frontends render no
 * E2EE affordances. NO media is actually encrypted today; these types describe
 * the data flow + storage shapes so the wiring is ready when the flag is later
 * turned on.
 *
 * CRYPTO MODEL (design target — implementation is stubbed/feature-gated):
 *   - Each student owns an asymmetric IDENTITY KEYPAIR (X25519). The PUBLIC key
 *     is stored in `users.e2ee_public_key`. The PRIVATE key is wrapped
 *     CLIENT-SIDE by a key derived from the student's password (Argon2id/PBKDF2,
 *     params in `users.e2ee_kdf_params`) and stored as opaque ciphertext in
 *     `users.e2ee_privkey_wrapped`. The server NEVER sees a plaintext private
 *     key — it only stores opaque strings + the public key.
 *   - The actual media key is a SYMMETRIC key (per room or device), identified
 *     by `device_tokens.e2ee_key_id`. It is shared to a student by ENCRYPTING
 *     IT TO THE STUDENT'S PUBLIC KEY; the resulting ciphertext is stored in
 *     `shared_keys.key_ciphertext` (opaque to the server). This works offline +
 *     multi-device.
 *   - QR flow: a device shows its media key as a QR (key id + key material) in
 *     its settings dialog. The student scans it, re-encrypts the key to their
 *     OWN public key, and POSTs the ciphertext so it is usable on every device.
 *   - `device_tokens.force_encryption`: when true, only students who possess the
 *     key may join (enforcement deferred; the flag is stored + surfaced now).
 */

/**
 * Global E2EE status as reported by `GET /e2ee/status` and surfaced on
 * `/auth/me`. When `enabled` is false the entire feature is inert.
 */
export interface E2eeStatus {
  enabled: boolean;
}

/**
 * LiveKit KeyProvider parity parameters.
 *
 * Phase 1 crypto contract. The publisher (web-device) and every subscriber
 * (web-student) MUST construct their LiveKit `ExternalE2EEKeyProvider` /
 * KeyProvider with the EXACT SAME options or the per-frame keys will not match
 * and decryption fails silently. These constants pin that shared configuration
 * so both sides import a single source of truth.
 *
 * - `ratchetSalt`: a FIXED, build-time constant (base64url). LiveKit feeds it
 *   into HKDF when deriving/ratcheting frame keys. It is NOT secret — its only
 *   job is to be identical on both ends. Do NOT randomize per-session.
 * - `keySize`: AES key size in BITS (128). Matches LiveKit's default AES-GCM
 *   128-bit frame cipher.
 *
 * MEDIA KEY DISTRIBUTION: the symmetric media key is 32 random bytes
 * (see `generateMediaKey` in the client lib) distributed as a base64url string
 * (the QR `key_material` / `shared_keys.key_ciphertext` plaintext-equivalent).
 * To install it into LiveKit the client decodes it to a `Uint8Array`/
 * `ArrayBuffer` (see `mediaKeyToArrayBuffer`) and calls the key provider's
 * `setKey(arrayBuffer)`. LiveKit then runs that material through its HKDF path
 * using `ratchetSalt` to derive the actual frame-encryption key. The 32-byte
 * length intentionally exceeds the 128-bit `keySize`; HKDF compresses it.
 */
export const E2EE_KEY_PROVIDER_OPTS = {
  /**
   * Fixed HKDF salt shared by publisher + subscriber (base64url, NOT secret).
   * Constant for the lifetime of the protocol version.
   */
  ratchetSalt: "ZGl5LWF2YXRhci1lMmVlLXYx",
  /** AES frame-cipher key size in bits. */
  keySize: 128,
} as const;

/**
 * The caller's E2EE identity. All three fields are opaque, client-produced
 * strings. The backend stores them verbatim and NEVER inspects/derives crypto
 * from them. `null` means "not yet set up".
 */
export interface E2eeIdentity {
  /** X25519 public key (base64url), or null when identity not set up. */
  public_key: string | null;
  /**
   * Client-side wrapped private key ciphertext (opaque base64url), or null.
   * The server MUST reject anything that looks like a plaintext private key.
   */
  privkey_wrapped: string | null;
  /**
   * KDF parameters used to derive the wrapping key from the password
   * (opaque JSON string), or null. e.g. {"kdf":"argon2id","m":...,"t":...,"salt":...}.
   */
  kdf_params: string | null;
}

/** GET /me/e2ee/identity response. */
export interface E2eeIdentityResponse {
  ok: true;
  identity: E2eeIdentity;
}

/**
 * PUT /me/e2ee/identity request. The wrapping happens CLIENT-SIDE; the backend
 * stores these opaque strings as-is. Never send a plaintext private key.
 */
export interface SetE2eeIdentityRequest {
  public_key: string;
  privkey_wrapped: string;
  kdf_params: string;
}

/**
 * The caller's E2EE RECOVERY blob. A second copy of the (already wrapped)
 * private key, this time wrapped with a key derived from a randomly-generated
 * RECOVERY CODE shown to the student once. It lets a student restore their
 * identity (and thus their previously-shared media keys) after a password change
 * / admin reset rotated the password-derived wrapping key. All fields are
 * opaque, client-produced strings; the server stores them verbatim and never
 * inspects them. `null` means "no recovery set up".
 */
export interface E2eeRecovery {
  /**
   * The private key wrapped with the recovery-code-derived key (opaque
   * base64url), or null. The server MUST reject anything that looks like a
   * plaintext private key.
   */
  recovery_wrapped: string | null;
  /** KDF params used to derive the recovery wrapping key (opaque JSON), or null. */
  recovery_kdf_params: string | null;
}

/** GET /me/e2ee/recovery response. */
export interface E2eeRecoveryResponse {
  ok: true;
  recovery: E2eeRecovery;
}

/**
 * PUT /me/e2ee/recovery request. The wrapping happens CLIENT-SIDE with the
 * recovery code; the backend stores these opaque strings as-is. `null` clears
 * the recovery blob (disable recovery).
 */
export interface SetE2eeRecoveryRequest {
  recovery_wrapped: string | null;
  recovery_kdf_params: string | null;
}

/** E2EE scope: a key is shared per room or per device. */
export type E2eeScope = "room" | "device";

/**
 * KDF parameters for wrapping the private key with a password-derived key.
 *
 * Serialized to JSON and stored opaquely in `users.e2ee_kdf_params`; the backend
 * never parses or interprets this — it round-trips the string verbatim. The
 * client (web-student) reads it back to re-derive the unwrapping key.
 *
 * Phase 1 uses libsodium Argon2id (`crypto_pwhash`, ALG_ARGON2ID13):
 * - `opslimit` / `memlimit` come from libsodium's INTERACTIVE/MODERATE presets.
 * - `salt` is a CSPRNG 16-byte value (base64url). It is REQUIRED to unwrap; it
 *   is embedded in the opaque wrapped blob too, but persisting it here keeps the
 *   params self-describing. Optional only for the "fresh params" return shape
 *   before a salt is bound.
 * - `parallelism` is recorded for forward-compat; libsodium's Argon2id always
 *   uses a single lane, so it is informational.
 */
export interface KdfParams {
  kdf: "argon2id";
  /** libsodium crypto_pwhash opslimit (CPU cost). */
  opslimit: number;
  /** libsodium crypto_pwhash memlimit in BYTES. */
  memlimit: number;
  /** Informational lane count (libsodium Argon2id uses 1). */
  parallelism: number;
  /** CSPRNG 16-byte salt (base64url). Required to unwrap. */
  salt?: string;
}

/**
 * One stored shared-key entry (a `shared_keys` row). `key_ciphertext` is the
 * symmetric media key ENCRYPTED to the owner's public key — opaque to the
 * server.
 */
export interface SharedKeyEntry {
  id: number;
  student_id: number;
  scope: E2eeScope;
  scope_id: number;
  key_ciphertext: string | null;
  shared_by: number | null;
  created_at: number;
  /**
   * Phase 6: the content-addressed key_id this row was sealed for, or null for
   * pre-Phase-6 rows. Lets the student compare the stored key against the
   * negotiated session key_id to detect a rotation (re-scan needed).
   */
  key_id?: string | null;
}

/** GET /me/e2ee/keys response — all keys the caller can load on login. */
export interface SharedKeysResponse {
  ok: true;
  keys: SharedKeyEntry[];
}

/**
 * POST /e2ee/shared-keys request. `student_id` defaults to the caller (self)
 * when omitted; staff/device callers may target a student in their room. The
 * `key_ciphertext` is encrypted-to-pubkey and opaque to the server.
 */
export interface StoreSharedKeyRequest {
  /** Target student. Omit (or self) for storing the caller's own key. */
  student_id?: number;
  scope: E2eeScope;
  scope_id: number;
  key_ciphertext: string;
  /**
   * Phase 6 (optional): the content-addressed key_id this ciphertext was sealed
   * for (from the scanned QR). Stored alongside the row so a later rotation can
   * revoke PRECISELY — a student who already re-scanned the new QR keeps their
   * fresh row while stale rows are deleted. Opaque, non-secret.
   */
  key_id?: string;
}

export interface StoreSharedKeyResponse {
  ok: true;
  entry: SharedKeyEntry;
}

/**
 * A device's E2EE settings. The backend stores ONLY the key id + force flag —
 * never the symmetric key plaintext (that lives device/client-side and is
 * conveyed via QR).
 */
export interface DeviceE2eeSettings {
  /** Whether only key-bearing students may join (enforcement deferred). */
  force_encryption: boolean;
  /** Identifier of the active media key, or null. */
  e2ee_key_id: string | null;
  /**
   * Device's Ed25519 SIGN public key (base64url), or null. Registered by the
   * device so a scanning student can verify the QR signature. Opaque to the
   * server; never a secret (it is a public verification key).
   */
  e2ee_sign_pubkey?: string | null;
}

/** GET /devices/:label/e2ee response. */
export interface DeviceE2eeResponse {
  ok: true;
  settings: DeviceE2eeSettings;
}

/** PATCH /devices/:label/e2ee request. All fields optional (partial update). */
export interface PatchDeviceE2eeRequest {
  force_encryption?: boolean;
  e2ee_key_id?: string | null;
  /** Device's Ed25519 sign public key (base64url) to register, or null. */
  e2ee_sign_pubkey?: string | null;
}

/**
 * GET /devices/:label/e2ee/sign-pubkey response. Lets a student who can VIEW the
 * device's room fetch the device's Ed25519 sign public key in order to verify a
 * scanned QR's signature. Returns null when the device hasn't registered one.
 */
export interface DeviceSignPubkeyResponse {
  ok: true;
  /** The device label/id the key belongs to. */
  device_id: string;
  /** Ed25519 sign public key (base64url), or null when unregistered. */
  sign_pubkey: string | null;
}

/**
 * The payload encoded into the device's QR code. The student app decodes this,
 * then re-encrypts `key_material` to its own public key before persisting via
 * `POST /e2ee/shared-keys`.
 *
 * AUTHENTICATION (Phase 1): the payload is signed by the device so a scanner can
 * verify it came from the genuine kiosk and has not expired. The signed fields
 * (`v`, `kind`, `scope`, `scope_id`, `key_id`, `key_material`, `exp`,
 * `device_id`) are CANONICALIZED before signing/verifying. The optional `sig`
 * and `exp`/`device_id` fields are additive — older scaffolding payloads without
 * them still PARSE; verification is a separate, explicit step.
 *
 * CANONICALIZATION CONTRACT (the actual sign/verify lives in the client libs):
 *   The canonical message is the UTF-8 JSON of an object containing ONLY the
 *   signed fields, with keys in this fixed order and `sig` EXCLUDED:
 *     { v, kind, scope, scope_id, key_id, key_material, exp, device_id }
 *   `sig` is `crypto_sign_detached(canonical, deviceSignSecretKey)` (Ed25519),
 *   base64url, and is verified with `crypto_sign_verify_detached` against the
 *   device's Ed25519 sign-public-key. See `canonicalizeQrPayload` /
 *   `verifyQrPayload` in `web-student/src/lib/e2ee.ts`.
 */
/**
 * Inputs to the join-time E2EE policy resolution. All three are evaluated at
 * the moment a student requests a join token. E2EE is SYMMETRIC and whole-
 * session: the result applies to the entire room session, negotiated server-
 * side at token issuance.
 *
 *  - `deviceForce`: the bound device's `force_encryption` flag.
 *  - `clientSupports`: the client's runtime `isE2EESupported()` result. This is
 *    the SOURCE OF TRUTH for capability — never browser/OS sniffing. (Copy may
 *    mention "recent Chrome / iOS not supported" as guidance only.)
 *  - `studentOptOut`: the student's persisted preference to receive plaintext.
 */
export interface E2eePolicyInputs {
  /** Bound device's force_encryption flag. */
  deviceForce: boolean;
  /** Runtime isE2EESupported() — authoritative capability signal. */
  clientSupports: boolean;
  /** Student's persisted opt-out preference (default false). */
  studentOptOut: boolean;
}

/**
 * Stable, machine-readable remediation code for a blocked join. The frontend
 * maps this to localized DE/EN copy; the backend also returns English fallback
 * text so a code it doesn't recognize is still actionable.
 */
export type E2eeRemediationCode = "e2ee_unsupported";

/**
 * Resolved E2EE session state for a join. When `blocked` is true NO token is
 * issued and `remediationCode` explains why; otherwise `active`/`forced`
 * describe the negotiated whole-session state.
 *
 *  - `active`: per-frame E2EE is ON for the whole session.
 *  - `forced`: the device forces E2EE; the student CANNOT opt out (toggle
 *    disabled client-side).
 *  - `blocked`: the join is refused (device forces E2EE but the client cannot
 *    do insertable streams). `remediationCode` is set in this case.
 */
export interface E2eeSessionState {
  active: boolean;
  forced: boolean;
  blocked: boolean;
  remediationCode?: E2eeRemediationCode;
}

/**
 * PURE policy resolver — the single source of truth for the E2EE join policy,
 * imported by BOTH the backend (token issuance) and the frontend (overlay
 * toggle + pre-flight UX). No side effects; deterministic from its inputs.
 *
 * Policy table (E2EE symmetric, whole-session, negotiated at token issuance):
 *
 *   deviceForce && !clientSupports → BLOCK (refuse token + remediation).
 *   deviceForce &&  clientSupports → E2EE ON, student CANNOT opt out (forced).
 *  !deviceForce &&  clientSupports && !studentOptOut → E2EE ON (default
 *                                                       "always on if available").
 *  !deviceForce &&  clientSupports &&  studentOptOut → E2EE OFF (allowed).
 *  !deviceForce && !clientSupports → E2EE OFF (allowed, not forced).
 *
 * The iOS/lockout decision is made by `clientSupports` (runtime
 * isE2EESupported()), NEVER by browser/OS sniffing.
 */
export function resolveE2eeSessionState(
  inputs: E2eePolicyInputs,
): E2eeSessionState {
  const { deviceForce, clientSupports, studentOptOut } = inputs;
  if (deviceForce) {
    if (!clientSupports) {
      // Device forces E2EE but this client cannot do insertable streams →
      // refuse the join with a structured remediation code.
      return {
        active: false,
        forced: true,
        blocked: true,
        remediationCode: "e2ee_unsupported",
      };
    }
    // Forced + capable: E2EE ON, opt-out not permitted.
    return { active: true, forced: true, blocked: false };
  }
  // Not forced: E2EE is "always on if available" unless the student opts out.
  const active = clientSupports && !studentOptOut;
  return { active, forced: false, blocked: false };
}

/**
 * Whole-room negotiated encryption mode (Phase 2 — non-forced compatibility
 * downgrade). The room session is EITHER fully per-frame encrypted or fully
 * plaintext; there is no mixed state. Negotiated server-side at token issuance
 * and re-evaluated as participant presence changes.
 *
 *  - `"encrypted"`: every connected participant can decrypt → per-frame E2EE ON.
 *  - `"plaintext"`: at least one connected participant in a NON-forced room
 *    cannot decrypt (unsupported browser, or capable-but-keyless after the
 *    auto-seal heal window) → the whole session is downgraded to plaintext so
 *    that viewer can still watch. NEVER reachable for a forced room.
 */
export type EncryptionMode = "encrypted" | "plaintext";

/**
 * One live participant's E2EE capability snapshot, as tracked by the server's
 * in-memory per-room presence map and fed to {@link resolveRoomEncryptionMode}.
 */
export interface RoomParticipantPresence {
  /**
   * Runtime `isE2EESupported()` of the participant's client. `false` means the
   * browser cannot do insertable-streams E2EE (e.g. iOS/Safari).
   */
  e2eeSupported: boolean;
  /**
   * True when the participant is CAPABLE but still holds no usable media key
   * AFTER its auto-seal heal/retry window (the loud `e2ee_autoseal_failed`
   * signal). Such a participant cannot decrypt, so it forces a downgrade just
   * like an unsupported one.
   */
  keyless: boolean;
}

/**
 * Inputs to the pure whole-room encryption-mode resolver.
 *
 *  - `forced`: the room forces E2EE anywhere in the hierarchy (`force_e2ee` /
 *    `force_e2ee_qr` / device `force_encryption`). A forced room is NEVER
 *    downgraded — incapable clients are blocked at join (`403 e2ee_required`)
 *    rather than triggering a plaintext fallback.
 *  - `participants`: the room's currently-live participants (after presence TTL
 *    sweep). Order-independent.
 */
export interface RoomEncryptionModeInputs {
  /** True when the room forces E2EE anywhere in the hierarchy. */
  forced: boolean;
  /** Currently-live participants in the room. */
  participants: RoomParticipantPresence[];
}

/**
 * PURE whole-room encryption-mode resolver — the single source of truth for the
 * Phase 2 non-forced downgrade decision, imported by the backend (token
 * issuance + device ping) and (indirectly) reflected to the frontends. No side
 * effects; deterministic from its inputs.
 *
 * Rules (locked decisions §2):
 *   - forced room                → ALWAYS `"encrypted"` (never downgrade;
 *                                  incapable clients are blocked at join).
 *   - non-forced room, any live participant with `e2eeSupported === false`
 *     OR `keyless === true`       → `"plaintext"` (downgrade so that viewer can
 *                                  still watch).
 *   - non-forced room, all live participants capable + keyed → `"encrypted"`.
 *
 * An empty non-forced room resolves to `"encrypted"` (nothing forces a
 * downgrade).
 */
export function resolveRoomEncryptionMode(
  inputs: RoomEncryptionModeInputs,
): EncryptionMode {
  if (inputs.forced) return "encrypted";
  const anyIncapable = inputs.participants.some(
    (p) => p.e2eeSupported === false || p.keyless === true,
  );
  return anyIncapable ? "plaintext" : "encrypted";
}

/**
 * P3 (Option A — device auto-seal distribution).
 *
 * One entry in the device-readable enrollment roster. The kiosk fetches this via
 * `GET /device/e2ee/roster` (device bearer token), seals the current media key to
 * each `e2ee_public_key`, and uploads the sealed blobs via
 * `POST /device/e2ee/distribute`. Only students of the device's room who have
 * provisioned an identity (non-null pubkey) are listed.
 *
 * `e2ee_public_key` is a public X25519 key (base64url) — never a secret.
 */
export interface DeviceRosterEntry {
  /** The enrolled student's user id. */
  user_id: number;
  /** The student's X25519 public key (base64url). Always non-null in the roster. */
  e2ee_public_key: string;
}

/** GET /device/e2ee/roster response. Students without a pubkey are omitted. */
export interface DeviceRosterResponse {
  ok: true;
  roster: DeviceRosterEntry[];
}

/**
 * One sealed key to install for a student. `key_ciphertext` is the symmetric
 * media key sealed to that student's public key (`crypto_box_seal`) — opaque to
 * the server, which stores it verbatim under `shared_keys` scope=`device`,
 * scope_id=0, key_id=`<current>`. The server MUST reject anything resembling a
 * plaintext key.
 */
export interface DistributeSharedKeyItem {
  /** Target student (MUST be a student of the device's room). */
  student_id: number;
  /** Media key sealed to the student's pubkey (opaque base64url). */
  key_ciphertext: string;
  /** Content-addressed key_id of the sealed media key (non-secret). */
  key_id: string;
}

/**
 * POST /device/e2ee/distribute request body: an array of per-student sealed keys
 * the kiosk produced from the roster. Frozen contract for P4 (which only adds a
 * `403 qr_required` guard — no signature change).
 */
export type DistributeSharedKeysRequest = DistributeSharedKeyItem[];

/** POST /device/e2ee/distribute response. `stored` = number of rows upserted. */
export interface DistributeSharedKeysResponse {
  ok: true;
  stored: number;
}

export interface E2eeQrPayload {
  /** Discriminator so the scanner can validate it parsed an E2EE QR. */
  v: 1;
  kind: "e2ee-key";
  scope: E2eeScope;
  /** Room id or device-key reference the key belongs to. */
  scope_id: number;
  /** Key identifier (matches `device_tokens.e2ee_key_id`). */
  key_id: string;
  /**
   * Symmetric media key material (base64url). REAL 32-byte key in Phase 1 (see
   * `generateMediaKey`); no longer a placeholder.
   */
  key_material: string;
  /**
   * Unix epoch MILLISECONDS after which the QR is rejected. Optional for
   * backward-compatible parsing; when present, verification fails if elapsed.
   */
  exp?: number;
  /** Identifier of the issuing device (for audit + key lookup). Optional. */
  device_id?: string;
  /**
   * Detached signature (base64url) over the canonical payload. Optional in the
   * type so unsigned scaffolding payloads still parse; verification requires it.
   */
  sig?: string;
}
